import React from "react";
import { Col, Row, Button } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { Chip, CircularProgress, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import TextFieldComponent from "../CustomUIComponents/TextFieldComponent/TextFieldComponent";
import { Strings } from "../../resources/i18n/i18n";
import { OPERATION_STATUS } from "../../config/axios.init";
import {
  validateBulkUploadAction,
  uploadBulkCsvAction,
  processPendingBulkRowsAction,
} from "./DatabaseTableAction";
import { BULK_UPLOAD_TABS as TABS } from "./Constants";
import "./DatabaseTables.scss";

/**
 * Bulk onboarding of new minimarts / micro credit clients from CSV.
 *
 * Note this registers *new* records — it is not an edit path. The backend
 * requires an inclusive startRow/endRow on both the register and the
 * process-pending calls, so the range inputs are mandatory here too rather than
 * being defaulted silently.
 */
class MinimartBulkUpload extends React.Component {
  constructor(props) {
    super(props);
    this.fileInput = React.createRef();
    this.state = {
      activeTab: 0,
      file: null,
      startRow: "",
      endRow: "",
      loading: false,
      validated: false,
      successMessage: "",
      errorMessage: "",
    };
  }

  activeTabConfig = () => TABS[this.state.activeTab];

  handleTabChange = (index) => {
    // Each tab is a different CSV shape, so nothing carries across.
    this.setState({
      activeTab: index,
      file: null,
      startRow: "",
      endRow: "",
      validated: false,
      successMessage: "",
      errorMessage: "",
    });
  };

  handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    // A new file invalidates the previous validation, so registering locks again.
    this.setState({
      file,
      validated: false,
      successMessage: "",
      errorMessage: "",
    });
  };

  /** Returns the parsed range, or null after setting the relevant error. */
  parseRowRange = () => {
    const { startRow, endRow } = this.state;
    if (!startRow.toString().trim() || !endRow.toString().trim()) {
      this.setState({ errorMessage: Strings("bulkUploadRowRangeRequired") });
      return null;
    }
    const start = Number(startRow);
    const end = Number(endRow);
    if (!Number.isInteger(start) || !Number.isInteger(end) || start < 1 || end < start) {
      this.setState({ errorMessage: Strings("bulkUploadRowRangeInvalid") });
      return null;
    }
    return { start, end };
  };

  handleValidate = () => {
    const { file } = this.state;
    if (!file) {
      this.setState({ errorMessage: Strings("bulkUploadSelectFile") });
      return;
    }
    this.runRequest(
      (done) => validateBulkUploadAction(this.activeTabConfig().validateUrl, file, done),
      Strings("bulkUploadValidateDone"),
      { validated: true }
    );
  };

  handleRegister = () => {
    const { file } = this.state;
    if (!file) {
      this.setState({ errorMessage: Strings("bulkUploadSelectFile") });
      return;
    }
    const range = this.parseRowRange();
    if (!range) {
      return;
    }
    this.runRequest(
      (done) =>
        uploadBulkCsvAction(
          this.activeTabConfig().uploadUrl,
          file,
          range.start,
          range.end,
          done
        ),
      Strings("bulkUploadRegisterDone")
    );
  };

  handleProcessPending = () => {
    const range = this.parseRowRange();
    if (!range) {
      return;
    }
    this.runRequest(
      (done) =>
        processPendingBulkRowsAction(
          this.activeTabConfig().processPendingUrl,
          range.start,
          range.end,
          done
        ),
      Strings("bulkUploadProcessPendingDone")
    );
  };

  runRequest = (invoke, successMessage, extraState = {}) => {
    this.setState({ loading: true, successMessage: "", errorMessage: "" }, () => {
      invoke((response) => {
        if (response.status === OPERATION_STATUS.SUCCESS) {
          this.setState({ loading: false, successMessage, ...extraState });
        } else {
          this.setState({
            loading: false,
            errorMessage:
              response.error?.message || Strings("errorFetchingData"),
          });
        }
      });
    });
  };

  renderMessages = () => {
    const { successMessage, errorMessage } = this.state;
    return (
      <>
        {successMessage && (
          <Row className="mb-2">
            <Col>
              <div className="successMessage">{successMessage}</div>
            </Col>
          </Row>
        )}
        {errorMessage && (
          <Row className="mb-2">
            <Col>
              <div className="errors p-2 errorMessage">{errorMessage}</div>
            </Col>
          </Row>
        )}
      </>
    );
  };

  renderExpectedHeaders = (tab) => (
    <div className="reversalNote">
      <InfoIcon className="reversalNoteIcon" />
      <div className="reversalNoteBody">
        <div className="reversalNoteTitle">{Strings("bulkUploadExpectedHeaders")}</div>
        <div className="pt-1">
          {tab.headers.map((header, index) => (
            <Chip
              key={header}
              size="small"
              className="me-1 mb-1"
              label={`${index + 1}. ${header}`}
            />
          ))}
        </div>
        <div className="reversalNoteText pt-2">{Strings("bulkUploadRangeNote")}</div>
      </div>
    </div>
  );

  renderTabContent = (tab) => {
    const { file, startRow, endRow, loading, validated } = this.state;
    return (
      <>
        <Row>
          <Col md={12} className="pb-2">
            <div className="listLabel">{Strings("bulkUploadDescription")}</div>
          </Col>
          <Col md={12} className="pb-2">
            {this.renderExpectedHeaders(tab)}
          </Col>
        </Row>

        <Row className="align-items-center">
          <Col md="auto" className="pt-2">
            <Button
              className="buttonBackground"
              onClick={() => this.fileInput.current.click()}
              disabled={loading}
            >
              {Strings("Browse")}
            </Button>
            <input
              ref={this.fileInput}
              type="file"
              className="collapseButton"
              accept=".csv"
              // Clearing the value lets the same file be picked again after a failed run.
              onClick={(event) => {
                event.target.value = null;
              }}
              onChange={this.handleFileChange}
            />
          </Col>

          <Col md={3} className="pt-2">
            <div className="listLabel">
              {file ? file.name : Strings("bulkUploadSelectFile")}
            </div>
          </Col>

          <Col md={2} className="pt-2">
            <TextFieldComponent
              type="number"
              placeholder={Strings("bulkUploadStartRow")}
              value={startRow}
              handleChange={(event) => this.setState({ startRow: event.target.value })}
            />
          </Col>

          <Col md={2} className="pt-2">
            <TextFieldComponent
              type="number"
              placeholder={Strings("bulkUploadEndRow")}
              value={endRow}
              handleChange={(event) => this.setState({ endRow: event.target.value })}
            />
          </Col>

          {loading && (
            <Col md="auto" className="pt-2">
              <CircularProgress size={22} />
            </Col>
          )}
        </Row>

        <Row className="pt-3">
          <Col md="auto">
            <Button
              className="buttonBackground"
              onClick={this.handleValidate}
              disabled={!file || loading}
            >
              {Strings("bulkUploadValidate")}
            </Button>
          </Col>

          <Col md="auto">
            {/* Registering creates records, so it is gated on a clean validation. */}
            <Tooltip title={validated ? "" : Strings("bulkUploadValidate")}>
              <span>
                <Button
                  className="buttonBackground"
                  onClick={this.handleRegister}
                  disabled={!validated || loading}
                >
                  {Strings("bulkUploadRegister")}
                </Button>
              </span>
            </Tooltip>
          </Col>

          <Col md="auto">
            <Button
              className="buttonBackground"
              onClick={this.handleProcessPending}
              disabled={loading}
            >
              {Strings("bulkUploadProcessPending")}
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  render() {
    const { activeTab } = this.state;

    return (
      <div className="duplicateEntryManagement">
        <div className="headerText">{Strings("MinimartBulkUpload")}</div>

        <Row className="paper">
          {this.renderMessages()}

          <Tabs selectedIndex={activeTab} onSelect={this.handleTabChange}>
            <TabList>
              {TABS.map((tab, index) => (
                <Tab
                  key={tab.key}
                  className={
                    activeTab === index
                      ? "react-tabs__tab react-tabs__tab--selected mt-1"
                      : "react-tabs__tab mt-1"
                  }
                >
                  {Strings(tab.label)}
                </Tab>
              ))}
            </TabList>

            {TABS.map((tab) => (
              <TabPanel key={tab.key}>
                <Row className="pt-2">
                  <Col md={12}>{this.renderTabContent(tab)}</Col>
                </Row>
              </TabPanel>
            ))}
          </Tabs>
        </Row>
      </div>
    );
  }
}

export default MinimartBulkUpload;
